import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import './LandingPage.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Se l'utente è già autenticato, reindirizzalo alla dashboard appropriata
  useEffect(() => {
    if (user) {
      // Determina la route di destinazione in base al ruolo
      if ([
        'admin',
        'uisp_territorial',
        'uisp_regional',
        'uisp_national',
        'uisp_territorial_secretary',
        'uisp_regional_secretary',
        'uisp_nazionale_secretary',
        'cordinator_territorial',
        'cordinator_regional',
        'cordinator_nazionale'
      ].includes(user.role)) {
        navigate('/admin', { replace: true });
      } else if ([
        'sda_territorial',
        'sda_regional',
        'sda_nazionale'
      ].includes(user.role)) {
        navigate('/admin/sda-dashboard', { replace: true });
      } else if (user.role === 'association') {
        navigate('/association', { replace: true });
      }
    }
  }, [user, navigate]);

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center py-5 min-vh-75">
            <Col lg={6} className="text-white mb-4 mb-lg-0">
              <div className="hero-content">
                <h1 className="display-3 fw-bold mb-4">
                  Sistema di Gestione<br />
                  <span className="text-uisp-primary">UISP</span>
                </h1>
                <p className="lead mb-4">
                  Piattaforma integrata per la gestione di associazioni sportive, 
                  tesseramenti ed eventi. Semplifica l'organizzazione e migliora 
                  l'esperienza dei tuoi associati.
                </p>
                <div className="d-flex gap-3 justify-content-lg-start justify-content-center">
                  <Button 
                    variant="light" 
                    size="lg" 
                    onClick={() => navigate('/login')}
                    className="px-4"
                    style={{ color: '#006e39', fontWeight: 600 }}
                  >
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    Accedi
                  </Button>
                </div>
              </div>
            </Col>
            <Col lg={6} className="d-none d-md-block text-center">
              <div className="hero-image">
                <i className="bi bi-people-fill display-1 text-white opacity-75"></i>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section py-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">Funzionalità Principali</h2>
              <p className="lead text-muted">
                Tutto ciò di cui hai bisogno per gestire la tua associazione sportiva
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            <Col md={6} lg={4}>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon mb-3">
                    <i className="bi bi-people-fill text-primary"></i>
                  </div>
                  <Card.Title className="h4 mb-3">Gestione Tesserati</Card.Title>
                  <Card.Text className="text-muted">
                    Gestisci facilmente l'anagrafica dei tesserati, i rinnovi e 
                    le membership multiple per diverse discipline sportive.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={4}>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon mb-3">
                    <i className="bi bi-calendar-event text-success"></i>
                  </div>
                  <Card.Title className="h4 mb-3">Gestione Eventi</Card.Title>
                  <Card.Text className="text-muted">
                    Crea, pianifica e gestisci eventi sportivi con facilità. 
                    Monitora le iscrizioni e organizza squadre e partecipanti.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={4}>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon mb-3">
                    <i className="bi bi-building text-info"></i>
                  </div>
                  <Card.Title className="h4 mb-3">Multi-Associazione</Card.Title>
                  <Card.Text className="text-muted">
                    Sistema completo per comitati territoriali, regionali e nazionali. 
                    Gestisci multiple associazioni da un'unica piattaforma.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={4}>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon mb-3">
                    <i className="bi bi-shield-check text-warning"></i>
                  </div>
                  <Card.Title className="h4 mb-3">Sicurezza</Card.Title>
                  <Card.Text className="text-muted">
                    Sistema sicuro con controllo accessi basato su ruoli, 
                    autenticazione JWT e protezione dei dati sensibili.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={4}>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon mb-3">
                    <i className="bi bi-file-earmark-spreadsheet text-danger"></i>
                  </div>
                  <Card.Title className="h4 mb-3">Import/Export</Card.Title>
                  <Card.Text className="text-muted">
                    Importa dati da Excel, esporta report e statistiche. 
                    Integrazione semplice con i tuoi strumenti esistenti.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={4}>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon mb-3">
                    <i className="bi bi-graph-up text-purple"></i>
                  </div>
                  <Card.Title className="h4 mb-3">Dashboard Analytics</Card.Title>
                  <Card.Text className="text-muted">
                    Dashboard interattive con statistiche in tempo reale, 
                    grafici e report per il monitoraggio delle attività.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center text-white">
              <h2 className="display-5 fw-bold mb-4">Pronto per iniziare?</h2>
              <p className="lead mb-4">
                Accedi al sistema per gestire la tua associazione e gli eventi sportivi
              </p>
              <Button 
                variant="light" 
                size="lg"
                onClick={() => navigate('/login')}
                className="px-5"
              >
                Accedi Ora
              </Button>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default LandingPage;
